<script lang="ts">
import {
  type ContractTypeExtractionEntry,
  TaskStatus,
  type ContractManagementExtractionInDb,
  type GaiaJsonValue,
} from '@/api';

/** One version's answers, for a "Show per version" column. */
export interface GaiaFieldsVersion {
  uid: string;
  /** Column head, e.g. "v2". */
  label: string;
  /** Under the head, e.g. "Jan 12, 2026". */
  meta: string;
  /** Older than the version the value column reads: its differing values are superseded. */
  older: boolean;
  /** Null while still loading. */
  entries: ContractTypeExtractionEntry[] | null;
}
</script>

<script setup lang="ts">
import { computed, ref, watch, type ComponentPublicInstance, type PropType } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { formatDisplayValue, isEffectivelyEmpty } from '@/utils/displayAiResponse';
import { isValueOverwritten } from '@/utils/overwrite';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaIconButton from '../../base/GaiaIconButton.vue';
import GaiaPopover from '../../base/GaiaPopover.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaTooltip from '../../base/GaiaTooltip.vue';
import GaiaPopconfirm from '../../composite/GaiaPopconfirm.vue';
import GaiaAgentReasoningCard from '../extraction/GaiaAgentReasoningCard.vue';
import GaiaOverwriteBadge from '../extraction/GaiaOverwriteBadge.vue';
import GaiaTypedValue from '../extraction/GaiaTypedValue.vue';
import { IconRefresh } from '../../icons';
import {
  gaiaAccent,
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaTracking,
} from '../../themes/tokens';

const props = defineProps({
  /** The contract type's prompts with the read version's answers. */
  entries: {
    type: Array as PropType<ContractTypeExtractionEntry[]>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** The file the answers were read from, for the reasoning card. */
  fileName: {
    type: String,
    required: true,
  },
  /** More than one version exists, so the per-version toggle shows. */
  canCompare: {
    type: Boolean,
    default: false,
  },
  /** Per-version columns are open (v-model:comparing). */
  comparing: {
    type: Boolean,
    default: false,
  },
  versions: {
    type: Array as PropType<GaiaFieldsVersion[]>,
    default: () => [],
  },
  /** The caller may rerun an extraction (platform admins only); hidden otherwise. */
  canRerun: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  edit: [extraction: ContractManagementExtractionInDb];
  openPage: [page: number, extraction: ContractManagementExtractionInDb];
  'update:comparing': [value: boolean];
  rerun: [extraction: ContractManagementExtractionInDb];
}>();

/** Rows shown before "Show N more". */
const LIMIT = 10;
const expanded = ref(false);

type RowState = 'unasked' | 'running' | 'failed' | 'missing' | 'found';

/** The value as read: a human overwrite wins over the agent's. */
function effectiveValue(extraction: ContractManagementExtractionInDb): GaiaJsonValue {
  return extraction.human_overwrite?.value ?? extraction.value;
}

function rowState(extraction: ContractManagementExtractionInDb | null | undefined): RowState {
  if (!extraction) return 'unasked';
  if (
    extraction.processing_state === TaskStatus.PENDING ||
    extraction.processing_state === TaskStatus.RUNNING
  ) {
    return 'running';
  }
  if (extraction.processing_state === TaskStatus.FAILED) return 'failed';
  return isEffectivelyEmpty(effectiveValue(extraction)) ? 'missing' : 'found';
}

/** The page a row jumps to — a human override wins over the agent's own page. */
function pageOf(extraction: ContractManagementExtractionInDb): number | null {
  return extraction.human_overwrite?.page === undefined
    ? extraction.page
    : extraction.human_overwrite.page;
}

function hasPageOverwrite(extraction: ContractManagementExtractionInDb): boolean {
  return isValueOverwritten(extraction.human_overwrite?.page, extraction.page);
}

function openRowPage(extraction: ContractManagementExtractionInDb): void {
  const page = pageOf(extraction);
  if (page !== null) emit('openPage', page, extraction);
}

function overwriteAuthor(extraction: ContractManagementExtractionInDb): string | undefined {
  const user = extraction.human_overwrite?.user;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must normalise to undefined
  return user?.full_name || user?.email || undefined;
}

const rows = computed(() =>
  props.entries
    .map(entry => {
      const extraction = entry.extraction ?? null;
      const state = rowState(extraction);
      return {
        key: entry.extraction_prompt.uid,

        label: entry.extraction_prompt.display_name || entry.extraction_prompt.name,
        /** The question asked of the document — shown on hovering the label. */
        prompt: entry.extraction_prompt.prompt,
        responseType: entry.extraction_prompt.response_type,
        extraction,
        state,
        formatted:
          extraction && state === 'found'
            ? formatDisplayValue(effectiveValue(extraction), entry.extraction_prompt.response_type)
            : null,
        overwritten:
          extraction !== null &&
          isValueOverwritten(extraction.human_overwrite?.value, extraction.value),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label))
);

const shownRows = computed(() => (expanded.value ? rows.value : rows.value.slice(0, LIMIT)));

const showVersions = computed(() => props.comparing && props.versions.length > 0);

/**
 * A row's values sit on one line; one cut off by the column gets "Expand",
 * which opens the row to its full values. Clipping is measured, not guessed
 * from the text: it depends on the column's width.
 */
const openRows = ref(new Set<string>());
const clippedRows = ref(new Set<string>());
const rowEls = new Map<string, HTMLElement>();
const grid = ref<HTMLElement | null>(null);

function setRowEl(key: string, el: Element | ComponentPublicInstance | null): void {
  if (el instanceof HTMLElement) rowEls.set(key, el);
  else rowEls.delete(key);
}

function measureClipping(): void {
  const clipped = new Set<string>();
  for (const [key, row] of rowEls) {
    // An open row no longer clips, but keeps its "Collapse".
    if (openRows.value.has(key)) {
      clipped.add(key);
      continue;
    }
    const cells = row.querySelectorAll<HTMLElement>('[data-clip]');
    if (Array.from(cells).some(cell => cell.scrollWidth > cell.clientWidth)) clipped.add(key);
  }
  clippedRows.value = clipped;
}

function toggleRow(key: string): void {
  const open = new Set(openRows.value);
  if (!open.delete(key)) open.add(key);
  openRows.value = open;
  measureClipping();
}

useResizeObserver(grid, measureClipping);
watch([shownRows, showVersions], measureClipping, { flush: 'post' });

/** A version's cell for a row: its formatted value, and how it stands to the value column. */
function versionCell(
  version: GaiaFieldsVersion,
  row: (typeof rows.value)[number]
): { text: string; tone: 'ink' | 'subtle' | 'muted'; superseded: boolean; title: string } {
  if (!version.entries) return { text: '…', tone: 'muted', superseded: false, title: 'Loading' };
  const entry = version.entries.find(e => e.extraction_prompt.uid === row.key);
  const extraction = entry?.extraction ?? null;
  if (!extraction || rowState(extraction) !== 'found') {
    return { text: '–', tone: 'muted', superseded: false, title: 'Not in this version' };
  }
  const text = formatDisplayValue(effectiveValue(extraction), row.responseType);
  if (text === row.formatted) return { text, tone: 'ink', superseded: false, title: 'Same value' };
  return version.older
    ? { text, tone: 'subtle', superseded: true, title: 'Superseded by a later version' }
    : { text, tone: 'subtle', superseded: false, title: 'Differs from the value shown' };
}

/**
 * The source track is fixed, not `max-content`: every row is a grid of its
 * own, so a content-sized track would come out a different width per row (a
 * page chip, a hidden delete, the header's toggle) and drag the Field / Value
 * split out of line with the header. Wide enough for the "Show per version"
 * toggle when it heads the track, else for a page chip.
 */
const sourceTrack = computed(() => (props.canCompare ? 128 : 96));

/** Each column's minimum width (px) and how it shares the rest (fr; 0 = fixed). */
const tracks = computed<[number, number][]>(() =>
  showVersions.value
    ? [
        [160, 1],
        [140, 1],
        ...props.versions.map((): [number, number] => [140, 1]),
        [sourceTrack.value, 0],
      ]
    : [
        [140, 1],
        [120, 2],
        [sourceTrack.value, 0],
      ]
);

const gridColumns = computed(() =>
  tracks.value.map(([min, fr]) => (fr ? `minmax(${min}px,${fr}fr)` : `${min}px`)).join(' ')
);

/** `.head` / `.row` gap and side padding, in px — the width below adds them up. */
const GRID_GAP = 24;
const GRID_PAD = 24;
const gridGap = `${GRID_GAP}px`;
const gridPad = `${GRID_PAD}px`;

/**
 * Below this the table scrolls sideways — the tracks' minimums with the gaps
 * and padding between them, so the rows (their background, their right
 * padding) always span the grid rather than the grid spilling past them.
 * Tight enough that beside the document (the split's left pane) the page
 * references stay in view — they are what the split is for.
 */
const minWidth = computed(() => {
  const columns = tracks.value.reduce((sum, [min]) => sum + min, 0);
  return `${columns + GRID_GAP * (tracks.value.length - 1) + GRID_PAD * 2}px`;
});

const accentColor = gaiaAccent.base;
const blankColor = gaiaNeutral.textBlank;
const monoFont = gaiaFonts.mono;
const headBg = gaiaNeutral.surfaceSoft;
const divider = gaiaNeutral.borderDivider;
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const headColor = gaiaNeutral.textSubtle;
const headSize = gaiaFontSize.micro;
const headWeight = String(gaiaFontWeight.bold);
const headTracking = gaiaTracking.label;
</script>

<template>
  <!-- The Fields tab of the Contract View mock: one row per prompt of the
       contract's type — Field | Value, and with "Show per version" one column
       per version, older differing values struck through — and each row's
       page reference, which opens the document beside the table. A row is one
       line; longer labels and values end in an ellipsis, and a row whose
       values are cut off offers "Expand" to wrap them in full. Hovering a
       value opens the agent's reasoning; clicking a row opens the overwrite
       modal. -->
  <div class="fields">
    <div class="scroll">
      <div ref="grid" :style="{ minWidth }">
        <!-- The head names the columns; a file with versions to compare also
             gets the per-version toggle and the version columns' heads. -->
        <div
          v-if="props.loading || rows.length > 0"
          class="head"
          :style="{ gridTemplateColumns: gridColumns }"
        >
          <span class="head-label">Question</span>
          <span class="head-label">{{ showVersions ? 'Answer shown' : 'Answer' }}</span>
          <template v-if="showVersions">
            <n-flex v-for="version in props.versions" :key="version.uid" vertical :size="2">
              <gaia-text size="dense" weight="semibold" tone="ink" truncate>
                {{ version.label }}
              </gaia-text>
              <gaia-text size="micro" tone="tertiary" truncate>{{ version.meta }}</gaia-text>
            </n-flex>
          </template>
          <n-flex justify="end">
            <gaia-button
              v-if="props.canCompare"
              size="tiny"
              @click="emit('update:comparing', !props.comparing)"
            >
              {{ props.comparing ? 'Hide per version' : 'Show per version' }}
            </gaia-button>
          </n-flex>
        </div>

        <template v-if="props.loading">
          <div
            v-for="index in 4"
            :key="index"
            class="row"
            :style="{ gridTemplateColumns: gridColumns }"
          >
            <span class="shimmer" />
            <span class="shimmer soft" />
          </div>
        </template>

        <div
          v-for="row in shownRows"
          v-else
          :key="row.key"
          :ref="el => setRowEl(row.key, el)"
          class="row"
          :class="{ clickable: row.extraction }"
          :style="{ gridTemplateColumns: gridColumns }"
          @click="row.extraction && emit('edit', row.extraction)"
        >
          <!-- Hovering the label shows the question behind it, under its full
               name (the label itself may be cut off). -->
          <gaia-tooltip placement="top-start" :delay="200">
            <template #trigger>
              <gaia-text size="row" tone="ink" truncate :style="{ minWidth: 0 }">
                {{ row.label }}
              </gaia-text>
            </template>
            <n-flex vertical :size="4">
              <gaia-text size="dense" weight="semibold" tone="ink">{{ row.label }}</gaia-text>
              <gaia-text size="dense" tone="subtle">{{ row.prompt }}</gaia-text>
            </n-flex>
          </gaia-tooltip>

          <n-flex align="baseline" :size="12" :wrap="false" class="value">
            <gaia-text v-if="row.state === 'unasked'" size="row" tone="muted">
              Not extracted yet
            </gaia-text>
            <n-flex v-else-if="row.state === 'running'" align="center" :size="6" :wrap="false">
              <gaia-spinner :size="12" />
              <gaia-text size="row" tone="subtle">Extracting…</gaia-text>
            </n-flex>
            <gaia-text
              v-else-if="row.state === 'failed'"
              size="row"
              tone="alert"
              :title="row.extraction?.error_text ?? undefined"
            >
              Extraction failed
            </gaia-text>
            <gaia-popover
              v-else-if="row.extraction"
              trigger="hover"
              placement="bottom-start"
              :delay="200"
            >
              <template #trigger>
                <n-flex align="center" :size="7" :wrap="false" :style="{ minWidth: 0, flex: 1 }">
                  <gaia-text v-if="row.state === 'missing'" size="row" tone="muted">
                    Not found
                  </gaia-text>
                  <gaia-typed-value
                    v-else
                    :value="effectiveValue(row.extraction)"
                    :field-type="row.responseType"
                    :truncate="!openRows.has(row.key)"
                    data-clip
                  />
                  <gaia-overwrite-badge
                    v-if="row.overwritten"
                    :by="overwriteAuthor(row.extraction)"
                    :style="{ flex: 'none' }"
                  />
                </n-flex>
              </template>
              <gaia-agent-reasoning-card
                :reason="row.extraction.human_overwrite?.reason ?? row.extraction.reason"
                :highlights="
                  row.extraction.human_overwrite?.highlighted_text ??
                  row.extraction.highlighted_text
                "
                :file-name="props.fileName"
                :page="pageOf(row.extraction)"
                @open-page="
                  (page: number) => row.extraction && emit('openPage', page, row.extraction)
                "
              />
            </gaia-popover>
            <gaia-button
              v-if="clippedRows.has(row.key)"
              text
              size="small"
              :style="{ flex: 'none' }"
              @click.stop="toggleRow(row.key)"
            >
              {{ openRows.has(row.key) ? 'Collapse' : 'Expand' }}
            </gaia-button>
          </n-flex>

          <template v-if="showVersions">
            <span
              v-for="version in props.versions"
              :key="version.uid"
              :title="versionCell(version, row).title"
              :class="{ superseded: versionCell(version, row).superseded }"
            >
              <gaia-text
                size="row"
                :tone="versionCell(version, row).tone"
                :truncate="!openRows.has(row.key)"
                data-clip
              >
                {{ versionCell(version, row).text }}
              </gaia-text>
            </span>
          </template>

          <!-- The reference: the page the answer was read from. It opens the
               document beside the table at that page, the passage highlighted.
               The rerun leads it, shown under the pointer only; a running
               extraction can't be rerun until it settles. -->
          <n-flex align="center" justify="end" :size="6" :wrap="false" @click.stop>
            <gaia-popconfirm
              v-if="props.canRerun && row.extraction && row.state !== 'running'"
              tone="neutral"
              title="Rerun this extraction?"
              confirm-label="Rerun"
              @confirm="row.extraction && emit('rerun', row.extraction)"
            >
              <template #trigger>
                <gaia-icon-button
                  :dimension="22"
                  size="tiny"
                  quaternary
                  class="row-action"
                  title="Rerun extraction"
                  aria-label="Rerun extraction"
                >
                  <n-icon :size="11" :component="IconRefresh" />
                </gaia-icon-button>
              </template>
              Its answer is replaced{{
                row.extraction?.human_overwrite ? ', the manual overwrite included' : ''
              }}. Costs one credit.
            </gaia-popconfirm>
            <gaia-button
              v-if="row.extraction && pageOf(row.extraction) !== null"
              size="tiny"
              class="page-chip"
              :class="{ overwritten: hasPageOverwrite(row.extraction) }"
              :title="`Open the document at page ${pageOf(row.extraction)} and highlight the source text`"
              @click.stop="row.extraction && openRowPage(row.extraction)"
            >
              <template #icon>
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M4 2.5h5l3 3v8H4z" />
                  <path d="M9 2.5v3h3" />
                </svg>
              </template>
              p.{{ pageOf(row.extraction) }}
            </gaia-button>
            <gaia-text
              v-else-if="row.state === 'found' || row.state === 'missing'"
              size="control"
              class="no-page"
            >
              no page
            </gaia-text>
          </n-flex>
        </div>

        <template v-if="!props.loading && rows.length === 0">
          <slot name="empty">
            <n-flex justify="center" class="empty">
              <gaia-text size="row" tone="subtle">This contract type has no fields yet.</gaia-text>
            </n-flex>
          </slot>
        </template>
      </div>
    </div>

    <n-flex
      v-if="!props.loading && rows.length > LIMIT"
      align="center"
      justify="space-between"
      :size="16"
      :wrap="false"
      class="foot"
    >
      <gaia-text size="meta" tone="subtle" :style="{ fontVariantNumeric: 'tabular-nums' }">
        {{ shownRows.length }} of {{ rows.length }} fields
      </gaia-text>
      <gaia-button size="small" secondary @click="expanded = !expanded">
        {{ expanded ? 'Show less' : `Show ${rows.length - LIMIT} more` }}
      </gaia-button>
    </n-flex>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.scroll {
  overflow-x: auto;
}

.head,
.row {
  display: grid;
  gap: v-bind(gridGap);
  padding: 11px v-bind(gridPad);
}

.head {
  align-items: end;
  padding-top: 12px;
  padding-bottom: 12px;
  background: v-bind(headBg);
  border-bottom: 1px solid v-bind(divider);
}

.head-label {
  font-size: v-bind(headSize);
  font-weight: v-bind(headWeight);
  letter-spacing: v-bind(headTracking);
  text-transform: uppercase;
  color: v-bind(headColor);
}

.row {
  align-items: baseline;
  border-bottom: 1px solid v-bind(rowBorder);
}

.row.clickable {
  cursor: pointer;
}

.row.clickable:hover {
  background: v-bind(rowHover);
}

.value {
  min-width: 0;
  overflow-wrap: anywhere;
}

.superseded :deep(*) {
  text-decoration: line-through;
}

.shimmer {
  display: block;
  height: 12px;
  width: 70%;
  border-radius: 6px;
  background: v-bind(headBg);
}

.shimmer.soft {
  width: 45%;
}

.empty {
  padding: 28px 18px;
}

/* An overwritten page is a human's answer, so the chip carries the accent the
   overwrite badge uses rather than the neutral button edge. */
.page-chip.overwritten {
  border-color: v-bind(accentColor);
  color: v-bind(accentColor);
}

.no-page {
  color: v-bind(blankColor);
  font-family: v-bind(monoFont);
}

/* A field's rerun shows under the pointer only, so the rows read as values,
   not as a toolbar. Kept in the DOM, so focus reveals it. */
.row-action {
  opacity: 0;
}

.row:hover .row-action,
.row-action:focus-visible {
  opacity: 1;
}

.foot {
  height: 48px;
  padding: 0 24px;
}
</style>
